import React, { useEffect, useState } from "react"
import {
  MdFavoriteBorder,
  MdOutlineVisibility,
  MdOutlineShoppingCart,
} from "react-icons/md"
import { useTranslation } from "react-i18next"
import FastMovingBadgePreview from "./FastMovingBadgePreview"
import PriceSymbol from "./PriceSymbol"
import styles from "./WebItemCardPreview.module.css"

const FALLBACK_IMAGE =
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="268" height="210" viewBox="0 0 268 210"><rect fill="#f3f6f8" width="268" height="210"/><text x="50%" y="50%" fill="#9aafbc" font-family="sans-serif" font-size="14" text-anchor="middle" dy=".3em">No image</text></svg>`
  )

const stop = e => {
  e.preventDefault()
  e.stopPropagation()
}

const resolveImageSrc = (itemImage, fallbackImage) =>
  itemImage || fallbackImage || FALLBACK_IMAGE

/**
 * Read-only port of website-template ProductCard (structure, CSS, icons, SAR symbol).
 * Favourite / eye / add-to-cart are visual only (non-functional).
 */
const WebItemCardPreview = ({
  item,
  fallbackImage = "",
  fastMovingIconUrl,
  fastMovingSettingsEnabled,
}) => {
  const { t } = useTranslation()
  const badges = Array.isArray(item?.badges) ? item.badges : []
  const [imageSrc, setImageSrc] = useState(() =>
    resolveImageSrc(item?.image, fallbackImage)
  )

  useEffect(() => {
    setImageSrc(resolveImageSrc(item?.image, fallbackImage))
  }, [item?.image, fallbackImage])

  const handleImageError = () => {
    if (fallbackImage && imageSrc !== fallbackImage) {
      setImageSrc(fallbackImage)
      return
    }
    if (imageSrc !== FALLBACK_IMAGE) {
      setImageSrc(FALLBACK_IMAGE)
    }
  }

  return (
    <article className={styles.card}>
      <div className={styles.media}>
        {item?.discountPercent != null && item.discountPercent > 0 && (
          <span className={styles.discount}>
            <span className={styles.discountValue} dir="ltr">
              -{item.discountPercent}%
            </span>
          </span>
        )}
        <FastMovingBadgePreview
          isFastMoving={item?.isFastMoving}
          iconUrl={fastMovingIconUrl}
          settingsEnabled={fastMovingSettingsEnabled}
          className={styles.fastMovingBadge}
          size={28}
          placement="overlay"
          variant="plain"
        />
        <div className={styles.mediaActions}>
          <span
            className={styles.favourite}
            aria-hidden="true"
            onClick={stop}
            onMouseDown={stop}
          >
            <MdFavoriteBorder size={20} />
          </span>
          <span
            className={styles.quickView}
            aria-hidden="true"
            onClick={stop}
            onMouseDown={stop}
          >
            <MdOutlineVisibility size={20} />
          </span>
        </div>
        <div className={styles.imageWrap}>
          <img
            src={imageSrc}
            alt={item?.name || "Item"}
            onError={handleImageError}
          />
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.detailPanel}>
          <span className={styles.name}>{item?.name || "Item name"}</span>

          <div className={styles.pricing}>
            {item?.price != null && (
              <span className={styles.price}>
                <PriceSymbol className={styles.priceSymbol} size={16} />
                {Number(item.price).toFixed(2)}
              </span>
            )}
            {item?.compareAtPrice != null && (
              <span className={styles.compare}>
                {Number(item.compareAtPrice).toFixed(2)}
              </span>
            )}
          </div>

          <div className={styles.badges} aria-hidden={!badges.length}>
            {badges.map(badge => (
              <span
                key={badge}
                className={`${styles.badge} ${
                  String(badge).toLowerCase().includes("top")
                    ? styles.badgeTop
                    : styles.badgeSelling
                }`}
              >
                {badge}
              </span>
            ))}
          </div>

          <button
            type="button"
            className={styles.addBtn}
            tabIndex={-1}
            aria-disabled="true"
            onClick={stop}
            onMouseDown={stop}
          >
            <MdOutlineShoppingCart size={20} aria-hidden="true" />
            <span>{t("Add to Cart")}</span>
          </button>
        </div>
      </div>
    </article>
  )
}

export default WebItemCardPreview
